import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Modal, Badge, Form, Row, Col, Nav, Tab, Dropdown } from 'react-bootstrap';
import { FiFileText, FiRefreshCw, FiSearch, FiEye, FiEdit, FiTrash2, FiPlus, FiUpload, FiLayers, FiDownload, FiChevronDown } from 'react-icons/fi';
import { fetchActivityReferences, updateActivityReferenceRequest, createActivityReferenceRequest, deleteActivityReferenceRequest, fetchActivitySubcoveragesRequest, updateActivitySubcoverageRequest, deleteActivitySubcoverageRequest } from '../../store/actions/dataActions';
import { fetchTripsSubcoveragesRequest } from '../../store/actions/tripsActions';
import { getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import ExportSubcoveragesTripModal from '../../components/Common/ExportSubcoveragesTripModal';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import {
    buildSearchStorageKey,
    readPersistedPageState,
    savePersistedPageState,
} from '../../utils/searchStatePersistence';
import { buildPartnerSelectOptions } from '../../utils/partnerId';
import {
    TRIP_REF_SUBCOVERAGES_IMPORT_TEMPLATE_URL,
    TRIP_REF_SUBCOVERAGES_UPDATE_TEMPLATE_URL,
} from '../../utils/referencesSubcovImport';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminReference.css';

const REFERENCE_PAGE_STORAGE_KEY = buildSearchStorageKey('reference');

const EMPTY_REFERENCE_FILTERS = {
    name: '',
    activity_type: '',
    category: '',
    company_name: '',
};

const EMPTY_SUBCOVERAGE_FILTERS = {
    company_name: '',
    reference_name: '',
    category: '',
};

const readReferencePageState = () => {
    const saved = readPersistedPageState(REFERENCE_PAGE_STORAGE_KEY);
    if (!saved) return null;
    return {
        activeTab: saved.activeTab || 'reference',
        searchTerm: String(saved.searchTerm || ''),
        currentPage: Number(saved.currentPage) > 0 ? Number(saved.currentPage) : 1,
        itemsPerPage: Number(saved.itemsPerPage) > 0 ? Number(saved.itemsPerPage) : 10,
        filters: { ...EMPTY_REFERENCE_FILTERS, ...(saved.filters || {}) },
        companyPartnerSearch: String(saved.companyPartnerSearch || ''),
        tripSubcoveragesPage: Number(saved.tripSubcoveragesPage) > 0 ? Number(saved.tripSubcoveragesPage) : 1,
        tripSubcoveragesFilters: { ...EMPTY_SUBCOVERAGE_FILTERS, ...(saved.tripSubcoveragesFilters || {}) },
        tripSubcoveragesAppliedFilters: { ...EMPTY_SUBCOVERAGE_FILTERS, ...(saved.tripSubcoveragesAppliedFilters || {}) },
        tripCompanyPartnerSearch: String(saved.tripCompanyPartnerSearch || ''),
        activitySubcoveragesPage: Number(saved.activitySubcoveragesPage) > 0 ? Number(saved.activitySubcoveragesPage) : 1,
        activitySubcoveragesFilters: { ...EMPTY_SUBCOVERAGE_FILTERS, ...(saved.activitySubcoveragesFilters || {}) },
        activitySubcoveragesAppliedFilters: { ...EMPTY_SUBCOVERAGE_FILTERS, ...(saved.activitySubcoveragesAppliedFilters || {}) },
        activityCompanyPartnerSearch: String(saved.activityCompanyPartnerSearch || ''),
    };
};

const AdminReference = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const activityReferencesState = useSelector((state) => state.data.activityReferences);
    const { updateReference, createReference, deleteReference, updateActivitySubcoverage, deleteActivitySubcoverage } = useSelector((state) => state.data);
    const tripsSubcoveragesState = useSelector((state) => state.trips.subcoveragesTrip);
    const activitySubcoveragesState = useSelector((state) => state.data.activitySubcoverages);
    const savedPageStateRef = useRef(readReferencePageState());
    const savedPageState = savedPageStateRef.current;

    const [activeTab, setActiveTab] = useState(savedPageState?.activeTab ?? 'reference');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [records, setRecords] = useState([]);
    const [searchTerm, setSearchTerm] = useState(savedPageState?.searchTerm ?? '');
    const [currentPage, setCurrentPage] = useState(savedPageState?.currentPage ?? 1);
    const [itemsPerPage, setItemsPerPage] = useState(savedPageState?.itemsPerPage ?? 10);
    const [filters, setFilters] = useState(savedPageState?.filters ?? { ...EMPTY_REFERENCE_FILTERS });
    const [meta, setMeta] = useState({
        total: 0,
        skip: 0,
        limit: 10,
        page: 1,
        total_pages: 1,
    });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnersError, setPartnersError] = useState(null);
    const [companyPartnerSearch, setCompanyPartnerSearch] = useState(savedPageState?.companyPartnerSearch ?? '');
    const [tripCompanyPartnerSearch, setTripCompanyPartnerSearch] = useState(savedPageState?.tripCompanyPartnerSearch ?? '');
    const [activityCompanyPartnerSearch, setActivityCompanyPartnerSearch] = useState(savedPageState?.activityCompanyPartnerSearch ?? '');
    const [selectedPartnerId, setSelectedPartnerId] = useState('');

    const partnerCompanyOptions = useMemo(() => {
        const names = partners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [partners]);

    const partnerSelectOptions = useMemo(
        () => buildPartnerSelectOptions(partners),
        [partners]
    );

    const [formData, setFormData] = useState({
        name: '',
        category: '',
        prcp_is_cumulated: false,
        is_visible_reference: true,
        activity_type: '',
    });
    const [successMessage, setSuccessMessage] = useState(null);
    const [createSuccessMessage, setCreateSuccessMessage] = useState(null);

    // États pour Trip Subcoverages
    const [tripSubcoveragesPage, setTripSubcoveragesPage] = useState(savedPageState?.tripSubcoveragesPage ?? 1);
    const [tripSubcoveragesItemsPerPage] = useState(10);
    const [tripSubcoveragesFilters, setTripSubcoveragesFilters] = useState(
        savedPageState?.tripSubcoveragesFilters ?? { ...EMPTY_SUBCOVERAGE_FILTERS }
    );
    const [tripSubcoveragesAppliedFilters, setTripSubcoveragesAppliedFilters] = useState(
        savedPageState?.tripSubcoveragesAppliedFilters ?? {}
    );
    const [showTripSubcoveragesExportModal, setShowTripSubcoveragesExportModal] = useState(false);
    const [tripSubcoveragesExportSuccess, setTripSubcoveragesExportSuccess] = useState(null);
    const [activitySubcoveragesPage, setActivitySubcoveragesPage] = useState(savedPageState?.activitySubcoveragesPage ?? 1);
    const [activitySubcoveragesItemsPerPage] = useState(10);
    const [activitySubcoveragesFilters, setActivitySubcoveragesFilters] = useState(
        savedPageState?.activitySubcoveragesFilters ?? { ...EMPTY_SUBCOVERAGE_FILTERS }
    );
    const [activitySubcoveragesAppliedFilters, setActivitySubcoveragesAppliedFilters] = useState(
        savedPageState?.activitySubcoveragesAppliedFilters ?? {}
    );
    const [showActivityEditModal, setShowActivityEditModal] = useState(false);
    const [showActivityDeleteModal, setShowActivityDeleteModal] = useState(false);
    const [selectedActivitySubcoverageId, setSelectedActivitySubcoverageId] = useState(null);
    const [selectedActivitySubcoverageForDelete, setSelectedActivitySubcoverageForDelete] = useState(null);
    const [activitySubcoverageFormData, setActivitySubcoverageFormData] = useState({
        sub_coverage_start_date: '',
        sub_coverage_end_date: '',
        trigger_sup: '',
        trigger_number_hours: '',
        unit: '',
        prcp_trigger_hour_inf: '',
        timing_hour: '',
        observation_timing_hour: '',
        trigger_hour_sup: '',
        timing_day: '',
        observation_timing_day: '',
        siv_ratio_maintained: '',
        siv_ratio_cancelled: '',
        city: '',
        zip_code: '',
        activity_address: '',
        country: '',
        trigger_type: '',
        coverage_code: '',
        orakle_price_ratio_excl_tsca: '',
        data_source: '',
        trigger_low: '',
        geo_longitude: '',
        geo_latitude: '',
    });

    const getDeep = (obj, path, fallback = 'N/A') => {
        try {
            const val = path.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
            return val ?? fallback;
        } catch {
            return fallback;
        }
    };

    const handleDetails = (item) => {
        const referenceId = getDeep(item, ['reference_id'], null);
        if (referenceId) {
            navigate(`/admin/reference/${referenceId}`);
        }
    };

    const handleEdit = (item) => {
        setSelectedItem(item);
        setSuccessMessage(null);
        // Initialiser le formulaire avec les données de l'item
        setFormData({
            name: getDeep(item, ['name'], ''),
            category: getDeep(item, ['category'], ''),
            prcp_is_cumulated: getDeep(item, ['prcp_is_cumulated'], false),
            is_visible_reference: getDeep(item, ['is_visible_reference'], true),
            activity_type: getDeep(item, ['activity_type'], ''),
        });
        setShowEditModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmitEdit = (e) => {
        e.preventDefault();
        if (selectedItem) {
            const referenceId = getDeep(selectedItem, ['reference_id'], null);
            if (referenceId) {
                dispatch(updateActivityReferenceRequest(referenceId, formData));
            }
        }
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSuccessMessage(null);
        setSelectedItem(null);
    };

    const handleCreate = () => {
        setSelectedPartnerId('');
        setCreateSuccessMessage(null);
        setFormData({
            name: '',
            category: '',
            prcp_is_cumulated: false,
            is_visible_reference: true,
            activity_type: '',
        });
        setShowCreateModal(true);
    };

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
        setCreateSuccessMessage(null);
        setSelectedPartnerId('');
        setFormData({
            name: '',
            category: '',
            prcp_is_cumulated: false,
            is_visible_reference: true,
            activity_type: '',
        });
    };

    const handleSubmitCreate = (e) => {
        e.preventDefault();
        if (!selectedPartnerId) {
            setCreateSuccessMessage('Veuillez sélectionner un partenaire');
            return;
        }
        if (!formData.name.trim()) {
            setCreateSuccessMessage('Le nom est obligatoire');
            return;
        }
        const payload = { ...formData };
        dispatch(createActivityReferenceRequest(selectedPartnerId, payload));
    };

    const handleDelete = (item) => {
        setSelectedItem(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        const referenceId = selectedItem?.reference_id;
        if (referenceId) {
            dispatch(deleteActivityReferenceRequest(referenceId));
        }
    };

    const handleCloseDeleteModal = () => {
        if (deleteReference.loading) return;
        setShowDeleteModal(false);
        setSelectedItem(null);
    };

    const fetchData = (filtersToUse = filters) => {
        const companyName = (filtersToUse.company_name || companyPartnerSearch).trim();
        const skip = (currentPage - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
            ...Object.fromEntries(
                Object.entries({
                    name: searchTerm || filtersToUse.name,
                    activity_type: filtersToUse.activity_type,
                    category: filtersToUse.category,
                    company_name: companyName,
                }).filter(([_, value]) => value && value.trim() !== '')
            ),
        };
        dispatch(fetchActivityReferences(params));
    };

    const handleApplyReferenceFilters = () => {
        const companyName = (filters.company_name || companyPartnerSearch).trim();
        const nextFilters = { ...filters, company_name: companyName };
        setFilters(nextFilters);
        setCompanyPartnerSearch(companyName);
        setCurrentPage(1);
        fetchData(nextFilters);
    };

    const handleReferenceFilterKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleApplyReferenceFilters();
        }
    };

    const handleClearReferenceFilters = () => {
        setSearchTerm('');
        const emptyFilters = { ...EMPTY_REFERENCE_FILTERS };
        setFilters(emptyFilters);
        setCompanyPartnerSearch('');
        setCurrentPage(1);
        fetchData(emptyFilters);
    };

    const fetchPartners = async () => {
        try {
            setPartnersLoading(true);
            setPartnersError(null);
            const response = await getAllPartners();
            // La réponse de /partners est de la forme { users: [...] }
            let partnersList = [];
            if (response?.users && Array.isArray(response.users)) {
                partnersList = response.users;
            } else if (Array.isArray(response)) {
                partnersList = response;
            } else if (response?.partners && Array.isArray(response.partners)) {
                partnersList = response.partners;
            } else if (response?.data && Array.isArray(response.data)) {
                partnersList = response.data;
            }
            setPartners(partnersList);
        } catch (error) {
            console.error('Erreur lors du chargement des partenaires:', error);
            setPartners([]);
            setPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            setError('Erreur lors du chargement des partenaires');
        } finally {
            setPartnersLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchData();
            fetchPartners();
        }
        /* eslint-disable-next-line */
    }, [isAuthenticated]);

    useEffect(() => {
        const d = activityReferencesState.data;
        let list = [];
        let paginationMeta = {
            total: 0,
            skip: 0,
            limit: itemsPerPage,
            page: currentPage,
            total_pages: 1,
        };

        if (Array.isArray(d)) {
            list = d;
        } else if (d) {
            // Support nested shapes like { References: [...] } ou { data: [...], meta: {...} }
            list = d?.References || d?.data || d?.records || d?.items || [];

            // Extraire les métadonnées de pagination
            if (d?.meta) {
                paginationMeta = {
                    total: d.meta.total || 0,
                    skip: d.meta.skip || 0,
                    limit: d.meta.limit || itemsPerPage,
                    page: d.meta.page || currentPage,
                    total_pages: d.meta.total_pages || Math.ceil((d.meta.total || 0) / (d.meta.limit || itemsPerPage)),
                };
            } else if (d?.total !== undefined) {
                paginationMeta = {
                    total: d.total || 0,
                    skip: d.skip || 0,
                    limit: d.limit || itemsPerPage,
                    page: d.page || currentPage,
                    total_pages: d.total_pages || Math.ceil((d.total || 0) / (d.limit || itemsPerPage)),
                };
            }
        }
        setRecords(Array.isArray(list) ? list : []);
        setMeta(paginationMeta);
        setLoading(activityReferencesState.loading);
        setError(activityReferencesState.error);
    }, [activityReferencesState, itemsPerPage, currentPage]);

    useEffect(() => {
        if (updateReference.success) {
            setSuccessMessage('Référence modifiée avec succès !');
            fetchData(); // Recharger les données
            const timer = setTimeout(() => {
                setShowEditModal(false);
                setSuccessMessage(null);
                setSelectedItem(null);
            }, 2000);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateReference.success]);

    useEffect(() => {
        if (createReference.success) {
            setCreateSuccessMessage('Référence créée avec succès !');
            fetchData(); // Recharger les données
            const timer = setTimeout(() => {
                setShowCreateModal(false);
                setCreateSuccessMessage(null);
                setSelectedPartnerId('');
                setFormData({
                    name: '',
                    category: '',
                    prcp_is_cumulated: false,
                    is_visible_reference: true,
                    activity_type: '',
                });
            }, 2000);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createReference.success]);

    useEffect(() => {
        if (deleteReference.success) {
            setShowDeleteModal(false);
            setSelectedItem(null);
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteReference.success]);

    // Utiliser directement les records car la pagination et le filtrage sont faits côté serveur
    const currentItems = records;
    const totalPages = meta.total_pages || 1;
    const totalItems = meta.total || records.length;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = indexOfFirstItem + records.length;

    // Les filtres ne se déclenchent plus automatiquement, seulement via le bouton "Appliquer les filtres"

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage]);

    const handlePageChange = (page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); };

    const renderPaginationItems = () => {
        const items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage < maxVisible - 1) startPage = Math.max(1, endPage - maxVisible + 1);
        if (startPage > 1) { items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>); if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" />); }
        for (let i = startPage; i <= endPage; i++) { items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>{i}</Pagination.Item>); }
        if (endPage < totalPages) { if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" />); items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>); }
        return items;
    };

    // Fonctions pour Trip Subcoverages
    const loadTripSubcoverages = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * tripSubcoveragesItemsPerPage;
        const params = {
            skip,
            limit: tripSubcoveragesItemsPerPage,
        };

        const activeFilters = filtersToUse !== null ? filtersToUse : tripSubcoveragesAppliedFilters;

        // Seulement les paramètres supportés par l'API /api/pagination/subcoverages-trip
        if (activeFilters.company_name && activeFilters.company_name.trim() !== '') {
            params.company_name = activeFilters.company_name.trim();
        }
        if (activeFilters.reference_name && activeFilters.reference_name.trim() !== '') {
            params.reference_name = activeFilters.reference_name.trim();
        }
        if (activeFilters.category && activeFilters.category.trim() !== '') {
            params.category = activeFilters.category.trim();
        }

        dispatch(fetchTripsSubcoveragesRequest(params));
    }, [dispatch, tripSubcoveragesItemsPerPage, tripSubcoveragesAppliedFilters]);

    useEffect(() => {
        if (isAuthenticated && activeTab === 'trip-subcoverage') {
            loadTripSubcoverages(tripSubcoveragesPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, activeTab, tripSubcoveragesPage]);

    const tripSubcoveragesData = useMemo(() => {
        const raw = tripsSubcoveragesState?.data;
        if (!raw) return [];
        return raw?.subcoverages_trip || raw?.data || raw || [];
    }, [tripsSubcoveragesState?.data]);

    const tripSubcoveragesArray = Array.isArray(tripSubcoveragesData) ? tripSubcoveragesData : [];

    const tripSubcoveragesMeta = useMemo(() => {
        return tripsSubcoveragesState?.data?.meta || {
            total: 0,
            skip: 0,
            limit: tripSubcoveragesItemsPerPage,
            page: tripSubcoveragesPage,
            total_pages: 0,
        };
    }, [tripsSubcoveragesState?.data, tripSubcoveragesItemsPerPage, tripSubcoveragesPage]);

    const tripSubcoveragesTotalPages = tripSubcoveragesMeta.total_pages || Math.max(1, Math.ceil((tripSubcoveragesMeta.total || 0) / tripSubcoveragesItemsPerPage));
    const tripSubcoveragesTotalItems = tripSubcoveragesMeta.total || 0;
    const tripSubcoveragesIndexOfFirstItem = tripSubcoveragesMeta.skip || 0;
    const tripSubcoveragesIndexOfLastItem = Math.min(tripSubcoveragesIndexOfFirstItem + tripSubcoveragesArray.length, tripSubcoveragesTotalItems);

    const handleTripSubcoveragesRefresh = () => {
        loadTripSubcoverages(tripSubcoveragesPage);
    };

    const handleTripSubcoveragesFilterChange = (e) => {
        const { name, value } = e.target;
        setTripSubcoveragesFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleTripSubcoveragesApplyFilters = () => {
        const companyName = (tripSubcoveragesFilters.company_name || tripCompanyPartnerSearch).trim();
        const nextFilters = { ...tripSubcoveragesFilters, company_name: companyName };
        setTripSubcoveragesFilters(nextFilters);
        setTripCompanyPartnerSearch(companyName);
        setTripSubcoveragesAppliedFilters(nextFilters);
        setTripSubcoveragesPage(1);
        loadTripSubcoverages(1, nextFilters);
    };

    const handleTripSubcoveragesKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleTripSubcoveragesApplyFilters();
        }
    };

    const handleTripSubcoveragesClearFilters = () => {
        const emptyFilters = { ...EMPTY_SUBCOVERAGE_FILTERS };
        setTripSubcoveragesFilters(emptyFilters);
        setTripCompanyPartnerSearch('');
        setTripSubcoveragesAppliedFilters(emptyFilters);
        setTripSubcoveragesPage(1);
        setTimeout(() => {
            loadTripSubcoverages(1, emptyFilters);
        }, 100);
    };

    const getTripSubcoveragesPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, tripSubcoveragesPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(tripSubcoveragesTotalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    const handleTripSubcoveragesDetails = (item) => {
        const subcoverageId = getDeep(item, ['subcoverage_trip', 'subcoverage_trip_id'], null);
        if (subcoverageId) {
            navigate(`/admin/trips/subcoverages/${subcoverageId}`);
        }
    };

    const handleTripSubcoveragesEdit = (item) => {
        const subcoverageId = getDeep(item, ['subcoverage_trip', 'subcoverage_trip_id'], null);
        if (subcoverageId) {
            navigate(`/admin/trips/subcoverages/${subcoverageId}/edit`);
        }
    };

    // Fonctions pour Activity Subcoverages
    const convertToDateInput = (value) => {
        if (!value || typeof value !== 'string') return '';
        const [day, month, year] = value.split('/');
        if (!day || !month || !year) return value;
        return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    };

    const convertToApiDate = (value) => {
        if (!value || typeof value !== 'string') return value;
        const [year, month, day] = value.split('-');
        if (!year || !month || !day) return value;
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year.padStart(4, '0')}`;
    };

    const loadActivitySubcoverages = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * activitySubcoveragesItemsPerPage;
        const params = {
            skip,
            limit: activitySubcoveragesItemsPerPage,
        };

        const activeFilters = filtersToUse !== null ? filtersToUse : activitySubcoveragesAppliedFilters;
        Object.keys(activeFilters).forEach((key) => {
            const value = activeFilters[key];
            if (value && value.trim() !== '') {
                params[key] = value.trim();
            }
        });

        dispatch(fetchActivitySubcoveragesRequest(params));
    }, [dispatch, activitySubcoveragesItemsPerPage, activitySubcoveragesAppliedFilters]);

    useEffect(() => {
        if (isAuthenticated && activeTab === 'activity-subcoverage') {
            loadActivitySubcoverages(activitySubcoveragesPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, activeTab, activitySubcoveragesPage]);

    useEffect(() => {
        if (updateActivitySubcoverage.success) {
            setShowActivityEditModal(false);
            loadActivitySubcoverages(activitySubcoveragesPage);
        }
    }, [updateActivitySubcoverage.success, activitySubcoveragesPage, loadActivitySubcoverages]);

    useEffect(() => {
        if (deleteActivitySubcoverage.success) {
            setShowActivityDeleteModal(false);
            setSelectedActivitySubcoverageForDelete(null);
            loadActivitySubcoverages(activitySubcoveragesPage);
        }
    }, [deleteActivitySubcoverage.success, activitySubcoveragesPage, loadActivitySubcoverages]);

    const activitySubcoveragesData = useMemo(() => {
        const payload = activitySubcoveragesState?.data;
        if (!payload) return [];
        const possibleArrays = [payload.subcoverages, payload.data, payload.records];
        const found = possibleArrays.find((entry) => Array.isArray(entry));
        if (Array.isArray(found)) return found;
        if (Array.isArray(payload)) return payload;
        return [];
    }, [activitySubcoveragesState?.data]);

    const activitySubcoveragesMeta = useMemo(() => activitySubcoveragesState?.data?.meta || {
        total: 0,
        skip: 0,
        limit: activitySubcoveragesItemsPerPage,
        page: activitySubcoveragesPage,
        total_pages: 0,
    }, [activitySubcoveragesState?.data, activitySubcoveragesItemsPerPage, activitySubcoveragesPage]);

    const activitySubcoveragesTotalPages = activitySubcoveragesMeta.total_pages || Math.max(1, Math.ceil((activitySubcoveragesMeta.total || 0) / activitySubcoveragesItemsPerPage));
    const activitySubcoveragesTotalItems = activitySubcoveragesMeta.total || 0;
    const activitySubcoveragesIndexOfFirstItem = activitySubcoveragesMeta.skip || 0;
    const activitySubcoveragesIndexOfLastItem = Math.min(activitySubcoveragesIndexOfFirstItem + activitySubcoveragesData.length, activitySubcoveragesTotalItems);

    const handleActivitySubcoveragesRefresh = () => {
        loadActivitySubcoverages(activitySubcoveragesPage);
    };

    const handleActivitySubcoveragesFilterChange = (event) => {
        const { name, value } = event.target;
        setActivitySubcoveragesFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleActivitySubcoveragesApplyFilters = () => {
        const companyName = (activitySubcoveragesFilters.company_name || activityCompanyPartnerSearch).trim();
        const nextFilters = { ...activitySubcoveragesFilters, company_name: companyName };
        setActivitySubcoveragesFilters(nextFilters);
        setActivityCompanyPartnerSearch(companyName);
        setActivitySubcoveragesAppliedFilters(nextFilters);
        setActivitySubcoveragesPage(1);
        loadActivitySubcoverages(1, nextFilters);
    };

    const handleActivitySubcoveragesKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleActivitySubcoveragesApplyFilters();
        }
    };

    const handleActivitySubcoveragesClearFilters = () => {
        const empty = { ...EMPTY_SUBCOVERAGE_FILTERS };
        setActivitySubcoveragesFilters(empty);
        setActivityCompanyPartnerSearch('');
        setActivitySubcoveragesAppliedFilters(empty);
        setActivitySubcoveragesPage(1);
        setTimeout(() => {
            loadActivitySubcoverages(1, empty);
        }, 100);
    };

    const getActivitySubcoveragesPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, activitySubcoveragesPage - Math.floor(maxVisiblePages / 2));
        let endPage = startPage + maxVisiblePages - 1;

        if (endPage > activitySubcoveragesTotalPages) {
            endPage = activitySubcoveragesTotalPages;
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const pages = [];
        for (let page = startPage; page <= endPage; page += 1) {
            pages.push(page);
        }
        return pages;
    };

    const renderActivityValue = (value, fallback = 'N/A') => {
        if (value === undefined || value === null || value === '') {
            return fallback;
        }
        return value;
    };

    const openActivityEditModal = (item) => {
        const subcoverage = item?.subcoverage || {};
        setSelectedActivitySubcoverageId(subcoverage.sub_coverage_id || null);
        setActivitySubcoverageFormData({
            sub_coverage_start_date: convertToDateInput(subcoverage.sub_coverage_start_date) || '',
            sub_coverage_end_date: convertToDateInput(subcoverage.sub_coverage_end_date) || '',
            trigger_sup: subcoverage.trigger_sup || '',
            trigger_number_hours: subcoverage.trigger_number_hours ?? '',
            unit: subcoverage.unit || '',
            prcp_trigger_hour_inf: subcoverage.prcp_trigger_hour_inf || '',
            timing_hour: subcoverage.timing_hour ?? '',
            observation_timing_hour: subcoverage.observation_timing_hour ?? '',
            trigger_hour_sup: subcoverage.trigger_hour_sup || '',
            timing_day: subcoverage.timing_day ?? '',
            observation_timing_day: subcoverage.observation_timing_day ?? '',
            siv_ratio_maintained: subcoverage.siv_ratio_maintained || '',
            siv_ratio_cancelled: subcoverage.siv_ratio_cancelled || '',
            city: subcoverage.city || '',
            zip_code: subcoverage.zip_code || '',
            activity_address: subcoverage.activity_address || '',
            country: subcoverage.country || '',
            trigger_type: subcoverage.trigger_type || '',
            coverage_code: subcoverage.coverage_code || '',
            orakle_price_ratio_excl_tsca: subcoverage.orakle_price_ratio_excl_tsca || '',
            data_source: subcoverage.data_source || '',
            trigger_low: subcoverage.trigger_low || '',
            geo_longitude: subcoverage.geo_longitude || '',
            geo_latitude: subcoverage.geo_latitude || '',
        });
        setShowActivityEditModal(true);
    };

    const handleActivityEditChange = (event) => {
        const { name, value } = event.target;
        setActivitySubcoverageFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleActivityEditSubmit = (event) => {
        event.preventDefault();
        if (!selectedActivitySubcoverageId) return;

        const numberFields = ['trigger_number_hours', 'timing_hour', 'observation_timing_hour', 'timing_day', 'observation_timing_day'];
        const payload = Object.keys(activitySubcoverageFormData).reduce((acc, key) => {
            const rawValue = activitySubcoverageFormData[key];
            if (rawValue === '') {
                acc[key] = null;
                return acc;
            }
            if (numberFields.includes(key)) {
                const parsed = Number(rawValue);
                acc[key] = Number.isNaN(parsed) ? null : parsed;
            } else if (key === 'sub_coverage_start_date' || key === 'sub_coverage_end_date') {
                acc[key] = convertToApiDate(rawValue);
            } else {
                acc[key] = rawValue;
            }
            return acc;
        }, {});

        dispatch(updateActivitySubcoverageRequest(selectedActivitySubcoverageId, payload));
    };

    const openActivityDeleteModal = (item) => {
        const subcoverage = item?.subcoverage || {};
        setSelectedActivitySubcoverageForDelete(item);
        setShowActivityDeleteModal(true);
    };

    const handleCloseActivityDeleteModal = () => {
        setShowActivityDeleteModal(false);
        setSelectedActivitySubcoverageForDelete(null);
    };

    const handleConfirmActivityDelete = () => {
        if (selectedActivitySubcoverageForDelete) {
            const subcoverage = selectedActivitySubcoverageForDelete?.subcoverage || {};
            const subcoverageId = subcoverage.sub_coverage_id;
            if (subcoverageId) {
                dispatch(deleteActivitySubcoverageRequest(subcoverageId));
            }
        }
    };

    return (
        <div className="dashboard-page admin-reference-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiFileText className="me-2" />
                                Reference & subcoverage
                            </h2>
                            <p className="text-muted mb-0">Gérez les références et sous-couvertures</p>
                        </div>
                    </div>

                    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'reference')}>
                        <Nav variant="tabs" className="admin-ref-tabs">
                            <Nav.Item>
                                <Nav.Link eventKey="reference">Reference</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="trip-subcoverage">Trip Subcoverage</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="activity-subcoverage">Activity Subcoverage</Nav.Link>
                            </Nav.Item>
                        </Nav>

                        <Tab.Content>
                            <Tab.Pane eventKey="reference">
                                <div className="d-flex gap-2 mb-3">
                                    <Button variant="outline-primary" onClick={fetchData} disabled={loading}>
                                        <FiRefreshCw className={loading ? 'spinning' : ''} /> Actualiser
                                    </Button>
                                    <Button variant="primary" onClick={handleCreate}>
                                        <FiPlus /> Nouvelle Référence
                                    </Button>
                                </div>

                                {error && (<Alert variant="danger" className="mt-3">{error}</Alert>)}

                                <Card className="mb-3">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">Filtres de recherche</h6>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={handleClearReferenceFilters}
                                            >
                                                Réinitialiser
                                            </Button>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={handleApplyReferenceFilters}
                                            >
                                                Appliquer les filtres
                                            </Button>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">

                                            <Col md={3}>
                                                <Form.Group className="mb-0">
                                                    <Form.Label className="mb-1">Recherche (nom)</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Rechercher par nom..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                setCurrentPage(1);
                                                                fetchData();
                                                            }
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-0">
                                                    <Form.Label className="mb-1">Catégorie</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Filtrer par catégorie..."
                                                        value={filters.category}
                                                        onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                setCurrentPage(1);
                                                                fetchData();
                                                            }
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-0">
                                                    <Form.Label className="mb-1">Type d'activité</Form.Label>
                                                    <Form.Select
                                                        value={filters.activity_type}
                                                        onChange={(e) => setFilters(prev => ({ ...prev, activity_type: e.target.value }))}
                                                    >
                                                        <option value="">Tous</option>
                                                        <option value="trip">Trip</option>
                                                        <option value="activity">Activity</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Label className="mb-1">Nom de société</Form.Label>
                                                <PartnerCompanyAutocomplete
                                                    value={filters.company_name}
                                                    onChange={(companyName) => setFilters((prev) => ({ ...prev, company_name: companyName }))}
                                                    onInputChange={setCompanyPartnerSearch}
                                                    options={partnerCompanyOptions}
                                                    loading={partnersLoading}
                                                    error={partnersError}
                                                    onKeyDown={handleReferenceFilterKeyPress}
                                                    placeholder="Rechercher un partenaire…"
                                                />
                                            </Col>
                                        </Row>
                                        <div className="d-flex align-items-center gap-3">
                                            <span className="text-muted">Éléments par page :</span>
                                            <Form.Select
                                                size="sm"
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                                style={{ width: 'auto' }}
                                            >
                                                {[10, 20, 50, 100].map((value) => (
                                                    <option key={value} value={value}>
                                                        {value}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <span className="text-muted ms-auto">
                                                Page {currentPage} sur {totalPages} — Total : {totalItems}
                                            </span>
                                        </div>
                                    </Card.Body>
                                </Card>

                                <div className="table-responsive">
                                    <div className="table-scroll">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-3">Chargement des références...</p>
                                        </div>
                                    ) : records.length === 0 ? (
                                        <div className="text-center py-5">
                                            <FiFileText size={48} className="text-muted mb-3" />
                                            <p className="text-muted">{searchTerm ? 'Aucun enregistrement trouvé' : 'Aucun enregistrement disponible'}</p>
                                        </div>
                                    ) : (
                                        <Table hover striped className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>Code référence</th>
                                                    <th>Nom</th>
                                                    <th>Catégorie</th>
                                                    <th>Partenaire</th>
                                                    <th>Déclenchement cumulé</th>
                                                    <th className="text-end col-actions">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentItems.map((item, idx) => (
                                                    <tr key={getDeep(item, ['reference_id'], idx)}>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{getDeep(item, ['reference_code'], '-')}</td>
                                                        <td>{getDeep(item, ['name'], 'N/A')}</td>
                                                        <td>{getDeep(item, ['category'], 'N/A')}</td>
                                                        <td>{getDeep(item, ['partner_info', 'company_name'], 'N/A')}</td>
                                                        <td>
                                                            <Badge bg={getDeep(item, ['prcp_is_cumulated'], false) ? 'warning' : 'secondary'}>
                                                                {getDeep(item, ['prcp_is_cumulated'], false) ? 'Oui' : 'Non'}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-end col-actions">
                                                            <Button variant="outline-info" size="sm" className="me-2" onClick={() => handleDetails(item)} title="Détails" aria-label="Détails">
                                                                <FiEye />
                                                            </Button>
                                                            <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleEdit(item)} title="Modifier" aria-label="Modifier">
                                                                <FiEdit />
                                                            </Button>
                                                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(item)} title="Supprimer" aria-label="Supprimer">
                                                                <FiTrash2 />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                    </div>
                                </div>

                                {!loading && totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-4">
                                        <div className="text-muted">Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, totalItems)} sur {totalItems}</div>
                                        <Pagination>
                                            <Pagination.Prev disabled={currentPage === 1} onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)} />
                                            {renderPaginationItems()}
                                            <Pagination.Next disabled={currentPage === totalPages} onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)} />
                                        </Pagination>
                                    </div>
                                )}

                                {/* Modals */}
                                {/* Modal de création */}
                                <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="xl" dialogClassName="modal-xl-custom">
                                    <Modal.Header closeButton>
                                        <Modal.Title>Créer une nouvelle référence</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        {createSuccessMessage && (
                                            <Alert variant="success" className="mb-3">
                                                {createSuccessMessage}
                                            </Alert>
                                        )}
                                        {createReference.error && (
                                            <Alert variant="danger" className="mb-3">
                                                {createReference.error}
                                            </Alert>
                                        )}
                                        <Form onSubmit={handleSubmitCreate}>
                                            <Row>
                                                <Col md={12}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Partenaire *</Form.Label>
                                                        <Form.Select
                                                            value={selectedPartnerId}
                                                            onChange={(e) => setSelectedPartnerId(e.target.value)}
                                                            required
                                                            disabled={createReference.loading}
                                                        >
                                                            <option value="">Sélectionner un partenaire...</option>
                                                            {partnerSelectOptions.map(({ id, label }) => (
                                                                <option key={id} value={id}>
                                                                    {label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Nom *</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="name"
                                                            value={formData.name}
                                                            onChange={handleInputChange}
                                                            required
                                                            disabled={createReference.loading}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Catégorie *</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="category"
                                                            value={formData.category}
                                                            onChange={handleInputChange}
                                                            required
                                                            minLength={3}
                                                            maxLength={50}
                                                            disabled={createReference.loading}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Type d'activité *</Form.Label>
                                                        <Form.Select
                                                            name="activity_type"
                                                            value={formData.activity_type}
                                                            onChange={handleInputChange}
                                                            required
                                                            disabled={createReference.loading}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            <option value="trip">Trip</option>
                                                            <option value="activity">Activity</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            name="prcp_is_cumulated"
                                                            label="Déclenchement cumulé *"
                                                            checked={formData.prcp_is_cumulated}
                                                            onChange={handleInputChange}
                                                            disabled={createReference.loading}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            name="is_visible_reference"
                                                            label="Référence visible *"
                                                            checked={formData.is_visible_reference}
                                                            onChange={handleInputChange}
                                                            disabled={createReference.loading}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Form>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button
                                            variant="secondary"
                                            onClick={handleCloseCreateModal}
                                            disabled={createReference.loading}
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleSubmitCreate}
                                            disabled={createReference.loading}
                                        >
                                            {createReference.loading ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Création...
                                                </>
                                            ) : (
                                                'Créer'
                                            )}
                                        </Button>
                                    </Modal.Footer>
                                </Modal>

                                {/* Modal d'édition */}
                                <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
                                    <Modal.Header closeButton>
                                        <Modal.Title>Modifier la référence</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        {successMessage && (
                                            <Alert variant="success" className="mb-3">
                                                {successMessage}
                                            </Alert>
                                        )}
                                        {updateReference.error && (
                                            <Alert variant="danger" className="mb-3">
                                                {updateReference.error}
                                            </Alert>
                                        )}
                                        <Form onSubmit={handleSubmitEdit}>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Nom *</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="name"
                                                            value={formData.name}
                                                            onChange={handleInputChange}
                                                            required
                                                            disabled={updateReference.loading}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Catégorie *</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="category"
                                                            value={formData.category}
                                                            onChange={handleInputChange}
                                                            required
                                                            disabled={updateReference.loading}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Type d'activité</Form.Label>
                                                        <Form.Select
                                                            name="activity_type"
                                                            value={formData.activity_type}
                                                            onChange={handleInputChange}
                                                            disabled={updateReference.loading}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            <option value="trip">Trip</option>
                                                            <option value="activity">Activity</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            name="prcp_is_cumulated"
                                                            label="Déclenchement cumulé"
                                                            checked={formData.prcp_is_cumulated}
                                                            onChange={handleInputChange}
                                                            disabled={updateReference.loading}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            name="is_visible_reference"
                                                            label="Référence visible"
                                                            checked={formData.is_visible_reference}
                                                            onChange={handleInputChange}
                                                            disabled={updateReference.loading}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Form>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button
                                            variant="secondary"
                                            onClick={handleCloseEditModal}
                                            disabled={updateReference.loading}
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleSubmitEdit}
                                            disabled={updateReference.loading}
                                        >
                                            {updateReference.loading ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Modification...
                                                </>
                                            ) : (
                                                'Enregistrer'
                                            )}
                                        </Button>
                                    </Modal.Footer>
                                </Modal>

                                {/* Modal de suppression */}
                                <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                                    <Modal.Header closeButton>
                                        <Modal.Title>Confirmer la suppression</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        {deleteReference.error && (
                                            <Alert variant="danger" className="mb-3">
                                                {deleteReference.error}
                                            </Alert>
                                        )}
                                        <p>
                                            Êtes-vous sûr de vouloir supprimer la référence{' '}
                                            <strong>{selectedItem?.name}</strong>
                                            {selectedItem?.reference_code ? ` (${selectedItem.reference_code})` : ''} ?
                                        </p>
                                        <p className="text-muted mb-0">
                                            Cette action est irréversible.
                                        </p>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={deleteReference.loading}>
                                            Annuler
                                        </Button>
                                        <Button variant="danger" onClick={confirmDelete} disabled={deleteReference.loading || !selectedItem?.reference_id}>
                                            {deleteReference.loading ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Suppression...
                                                </>
                                            ) : (
                                                'Supprimer'
                                            )}
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                            </Tab.Pane>

                            <Tab.Pane eventKey="trip-subcoverage">
                                <div className="trip-subcoverage-action-bar" role="toolbar" aria-label="Actions Trip Subcoverage">
                                    <Button
                                        variant="primary"
                                        className="action-bar-btn action-bar-btn--primary"
                                        onClick={() => navigate('/admin/reference/subcoverages/import')}
                                    >
                                        <FiUpload className="action-bar-btn__icon" aria-hidden />
                                        Import ref et subcoverage
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        className="action-bar-btn action-bar-btn--secondary"
                                        onClick={() => setShowTripSubcoveragesExportModal(true)}
                                    >
                                        <FiDownload className="action-bar-btn__icon" aria-hidden />
                                        Exporter Subcoverages Trip
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        className="action-bar-btn action-bar-btn--secondary"
                                        onClick={() => navigate('/admin/reference/subcoverages/update-import')}
                                    >
                                        <FiEdit className="action-bar-btn__icon" aria-hidden />
                                        Mise à jour ref et subcoverage
                                    </Button>
                                    <Dropdown className="action-bar-dropdown" align="start">
                                        <Dropdown.Toggle
                                            variant="outline-secondary"
                                            id="trip-subcoverage-templates-dropdown"
                                            className="action-bar-btn action-bar-btn--secondary action-bar-btn--dropdown"
                                        >
                                            <span className="action-bar-btn__content">
                                                <FiLayers className="action-bar-btn__icon" aria-hidden />
                                                <span className="action-bar-btn__label">Templates Excel</span>
                                                <FiChevronDown className="action-bar-btn__chevron" aria-hidden />
                                            </span>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu
                                            className="action-bar-dropdown-menu"
                                            popperConfig={{ strategy: 'fixed' }}
                                            renderOnMount
                                        >
                                            <Dropdown.Item
                                                href={TRIP_REF_SUBCOVERAGES_IMPORT_TEMPLATE_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <FiDownload className="me-2" aria-hidden />
                                                Template import
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                                href={TRIP_REF_SUBCOVERAGES_UPDATE_TEMPLATE_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <FiDownload className="me-2" aria-hidden />
                                                Template mise à jour
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    <Button
                                        variant="outline-secondary"
                                        className="action-bar-btn action-bar-btn--secondary"
                                        onClick={handleTripSubcoveragesRefresh}
                                        disabled={tripsSubcoveragesState.loading}
                                    >
                                        <FiRefreshCw
                                            className={`action-bar-btn__icon${tripsSubcoveragesState.loading ? ' spinning' : ''}`}
                                            aria-hidden
                                        />
                                        Actualiser
                                    </Button>
                                </div>

                                {tripSubcoveragesExportSuccess && (
                                    <Alert variant="success" className="mt-3" onClose={() => setTripSubcoveragesExportSuccess(null)} dismissible>
                                        {tripSubcoveragesExportSuccess}
                                    </Alert>
                                )}

                                {tripsSubcoveragesState.error && (
                                    <Alert variant="danger" className="mt-3">
                                        {tripsSubcoveragesState.error}
                                    </Alert>
                                )}

                                <Card className="mb-3">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">Filtres de recherche</h6>
                                        <div className="d-flex gap-2">
                                            <Button variant="outline-secondary" size="sm" onClick={handleTripSubcoveragesClearFilters}>
                                                Réinitialiser
                                            </Button>
                                            <Button variant="primary" size="sm" onClick={handleTripSubcoveragesApplyFilters}>
                                                Appliquer les filtres
                                            </Button>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col md={4}>
                                                <Form.Label className="mb-1">Nom de société</Form.Label>
                                                <PartnerCompanyAutocomplete
                                                    value={tripSubcoveragesFilters.company_name}
                                                    onChange={(companyName) => setTripSubcoveragesFilters((prev) => ({ ...prev, company_name: companyName }))}
                                                    onInputChange={setTripCompanyPartnerSearch}
                                                    options={partnerCompanyOptions}
                                                    loading={partnersLoading}
                                                    error={partnersError}
                                                    onKeyDown={handleTripSubcoveragesKeyPress}
                                                    placeholder="Rechercher un partenaire…"
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-0">
                                                    <Form.Label className="mb-1">Nom de la référence</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="reference_name"
                                                        value={tripSubcoveragesFilters.reference_name}
                                                        onChange={handleTripSubcoveragesFilterChange}
                                                        onKeyPress={handleTripSubcoveragesKeyPress}
                                                        placeholder="Filtrer par référence..."
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-0">
                                                    <Form.Label className="mb-1">Catégorie</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="category"
                                                        value={tripSubcoveragesFilters.category}
                                                        onChange={handleTripSubcoveragesFilterChange}
                                                        onKeyPress={handleTripSubcoveragesKeyPress}
                                                        placeholder="Filtrer par catégorie..."
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                <div className="table-responsive">
                                    <div className="table-scroll">
                                    {tripsSubcoveragesState.loading ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-3">Chargement des sous-couvertures...</p>
                                        </div>
                                    ) : tripSubcoveragesArray.length === 0 ? (
                                        <div className="text-center py-5">
                                            <FiFileText size={48} className="text-muted mb-3" />
                                            <p className="text-muted">Aucune sous-couverture trouvée</p>
                                        </div>
                                    ) : (
                                        <Table hover striped className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>Code</th>
                                                    <th>Type</th>
                                                    <th>Heures déclenchement</th>
                                                    <th>Ville</th>
                                                    <th>Pays</th>
                                                    <th>Référence</th>
                                                    <th>Partenaire</th>
                                                    <th>Période efficace</th>
                                                    <th>Montant cash (Maintien)</th>
                                                    <th className="text-end col-actions">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tripSubcoveragesArray.map((item, index) => {
                                                    const subcoverage = getDeep(item, ['subcoverage_trip'], {});
                                                    const reference = getDeep(item, ['reference'], {});
                                                    const partner = getDeep(item, ['partner_info'], {});

                                                    return (
                                                        <tr key={getDeep(subcoverage, ['subcoverage_trip_id'], index)}>
                                                            <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{getDeep(subcoverage, ['subcoverage_trip_code'], '-')}</td>
                                                            <td>
                                                                <Badge bg="info" text="dark">
                                                                    {getDeep(subcoverage, ['trigger_type'], 'N/A')}
                                                                </Badge>
                                                            </td>
                                                            <td>{getDeep(subcoverage, ['trigger_number_hours'], 'N/A')}</td>
                                                            <td>{getDeep(subcoverage, ['city'], 'N/A')}</td>
                                                            <td>{getDeep(subcoverage, ['country'], 'N/A')}</td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <span>{getDeep(reference, ['name'], 'N/A')}</span>
                                                                    <small className="text-muted" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{getDeep(reference, ['reference_code'], '')}</small>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <span>{getDeep(partner, ['company_name'], 'N/A')}</span>
                                                                    <small className="text-muted">{`${getDeep(partner, ['first_name'], '')} ${getDeep(partner, ['last_name'], '')}`.trim() || ''}</small>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <span>{getDeep(subcoverage, ['sub_coverage_start_date'], 'N/A')}</span>
                                                                    <small className="text-muted">{getDeep(subcoverage, ['sub_coverage_end_date'], '')}</small>
                                                                </div>
                                                            </td>
                                                            <td>{getDeep(subcoverage, ['cash_share_maintain'], 'N/A')}</td>
                                                            <td className="text-end col-actions">
                                                                <Button variant="outline-info" size="sm" className="me-2" onClick={() => handleTripSubcoveragesDetails(item)} title="Détails" aria-label="Détails">
                                                                    <FiEye />
                                                                </Button>
                                                                <Button variant="outline-primary" size="sm" onClick={() => handleTripSubcoveragesEdit(item)} title="Modifier" aria-label="Modifier">
                                                                    <FiEdit />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    )}
                                    </div>
                                </div>

                                {!tripsSubcoveragesState.loading && tripSubcoveragesTotalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-4">
                                        <div className="text-muted">Affichage de {tripSubcoveragesIndexOfFirstItem + 1} à {Math.min(tripSubcoveragesIndexOfLastItem, tripSubcoveragesTotalItems)} sur {tripSubcoveragesTotalItems}</div>
                                        <Pagination>
                                            <Pagination.Prev disabled={tripSubcoveragesPage === 1} onClick={() => tripSubcoveragesPage > 1 && setTripSubcoveragesPage(tripSubcoveragesPage - 1)} />
                                            {(() => {
                                                const { startPage, endPage } = getTripSubcoveragesPaginationRange();
                                                const pages = [];
                                                for (let page = startPage; page <= endPage; page += 1) {
                                                    pages.push(
                                                        <Pagination.Item key={page} active={page === tripSubcoveragesPage} onClick={() => setTripSubcoveragesPage(page)}>
                                                            {page}
                                                        </Pagination.Item>
                                                    );
                                                }
                                                return pages;
                                            })()}
                                            <Pagination.Next disabled={tripSubcoveragesPage === tripSubcoveragesTotalPages} onClick={() => tripSubcoveragesPage < tripSubcoveragesTotalPages && setTripSubcoveragesPage(tripSubcoveragesPage + 1)} />
                                        </Pagination>
                                    </div>
                                )}
                            </Tab.Pane>

                            <Tab.Pane eventKey="activity-subcoverage">
                                <div className="d-flex gap-2 mb-3">
                                    <Button variant="outline-secondary" onClick={handleActivitySubcoveragesRefresh} disabled={activitySubcoveragesState.loading}>
                                        <FiRefreshCw className={activitySubcoveragesState.loading ? 'spinning' : ''} /> Actualiser
                                    </Button>
                                    <Button variant="primary" onClick={() => navigate('/activity/subcoverages/create')}>
                                        <FiPlus /> Ajouter une sous-couverture
                                    </Button>
                                </div>

                                {activitySubcoveragesState.error && (
                                    <Alert variant="danger" className="mt-3">
                                        {activitySubcoveragesState.error}
                                    </Alert>
                                )}
                                {updateActivitySubcoverage.success && (
                                    <Alert variant="success" className="mt-3">
                                        Sous-couverture mise à jour avec succès.
                                    </Alert>
                                )}
                                {updateActivitySubcoverage.error && (
                                    <Alert variant="danger" className="mt-3">
                                        {updateActivitySubcoverage.error}
                                    </Alert>
                                )}
                                {deleteActivitySubcoverage.success && (
                                    <Alert variant="success" className="mt-3">
                                        Sous-couverture supprimée avec succès.
                                    </Alert>
                                )}
                                {deleteActivitySubcoverage.error && (
                                    <Alert variant="danger" className="mt-3">
                                        {deleteActivitySubcoverage.error}
                                    </Alert>
                                )}

                                <Card className="mb-3">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">Filtres de recherche</h6>
                                        <div className="d-flex gap-2">
                                            <Button variant="outline-secondary" size="sm" onClick={handleActivitySubcoveragesClearFilters}>
                                                Réinitialiser
                                            </Button>
                                            <Button variant="primary" size="sm" onClick={handleActivitySubcoveragesApplyFilters}>
                                                Appliquer les filtres
                                            </Button>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Label>Nom de société</Form.Label>
                                                <PartnerCompanyAutocomplete
                                                    value={activitySubcoveragesFilters.company_name}
                                                    onChange={(companyName) => setActivitySubcoveragesFilters((prev) => ({ ...prev, company_name: companyName }))}
                                                    onInputChange={setActivityCompanyPartnerSearch}
                                                    options={partnerCompanyOptions}
                                                    loading={partnersLoading}
                                                    error={partnersError}
                                                    onKeyDown={handleActivitySubcoveragesKeyPress}
                                                    placeholder="Rechercher un partenaire…"
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Référence</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="reference_name"
                                                        value={activitySubcoveragesFilters.reference_name}
                                                        onChange={handleActivitySubcoveragesFilterChange}
                                                        onKeyPress={handleActivitySubcoveragesKeyPress}
                                                        placeholder="Nom de la référence"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Catégorie</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="category"
                                                        value={activitySubcoveragesFilters.category}
                                                        onChange={handleActivitySubcoveragesFilterChange}
                                                        onKeyPress={handleActivitySubcoveragesKeyPress}
                                                        placeholder="Catégorie"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                <Card>
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <span>
                                            Résultats : {activitySubcoveragesIndexOfFirstItem + 1} - {activitySubcoveragesIndexOfLastItem} sur {activitySubcoveragesTotalItems}
                                        </span>
                                        {activitySubcoveragesState.loading && <Spinner animation="border" size="sm" />}
                                    </Card.Header>
                                    <div className="table-responsive">
                                        <div className="table-scroll">
                                        <Table hover striped className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>Code couverture</th>
                                                    <th>Référence</th>
                                                    <th>Catégorie</th>
                                                    <th>Partenaire</th>
                                                    <th>Ville</th>
                                                    <th>Pays</th>
                                                    <th>Type</th>
                                                    <th>Période</th>
                                                    <th className="col-actions">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {activitySubcoveragesState.loading ? (
                                                    <tr>
                                                        <td colSpan={9} className="text-center py-4">
                                                            <Spinner animation="border" role="status" />
                                                        </td>
                                                    </tr>
                                                ) : activitySubcoveragesData.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={9} className="text-center py-4">
                                                            Aucune sous-couverture trouvée.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    activitySubcoveragesData.map((item, index) => {
                                                        const subcoverage = item?.subcoverage || {};
                                                        const reference = item?.reference || {};
                                                        const partner = item?.partner_info || {};

                                                        return (
                                                            <tr key={subcoverage.sub_coverage_id || index}>
                                                                <td>
                                                                    <div className="fw-semibold">{renderActivityValue(subcoverage.coverage_code)}</div>
                                                                    <div className="text-muted small">{renderActivityValue(subcoverage.sub_coverage_id)}</div>
                                                                </td>
                                                                <td>
                                                                    <div className="fw-semibold">{renderActivityValue(reference.name)}</div>
                                                                    <div className="text-muted small">{renderActivityValue(reference.reference_code)}</div>
                                                                </td>
                                                                <td>{renderActivityValue(reference.category)}</td>
                                                                <td>
                                                                    <div className="fw-semibold">{renderActivityValue(partner.company_name, `${renderActivityValue(partner.first_name, '')} ${renderActivityValue(partner.last_name, '')}`)}</div>
                                                                    <div className="text-muted small">{renderActivityValue(partner.user_code)}</div>
                                                                </td>
                                                                <td>{renderActivityValue(subcoverage.city)}</td>
                                                                <td>{renderActivityValue(subcoverage.country)}</td>
                                                                <td>
                                                                    <Badge bg="info" text="dark" className="text-capitalize">
                                                                        {renderActivityValue(subcoverage.trigger_type)}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    <div>{renderActivityValue(subcoverage.sub_coverage_start_date)}</div>
                                                                    <div className="text-muted small">{renderActivityValue(subcoverage.sub_coverage_end_date)}</div>
                                                                </td>
                                                                <td className="col-actions">
                                                                    <div className="d-flex gap-2">
                                                                        <Button
                                                                            variant="outline-info"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                if (subcoverage.sub_coverage_id) {
                                                                                    navigate(`/activity/subcoverages/${subcoverage.sub_coverage_id}`);
                                                                                }
                                                                            }}
                                                                        >
                                                                            <FiEye className="me-1" /> Détails
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            onClick={() => openActivityEditModal(item)}
                                                                        >
                                                                            <FiEdit className="me-1" /> Modifier
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => openActivityDeleteModal(item)}
                                                                        >
                                                                            <FiTrash2 className="me-1" /> Supprimer
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </Table>
                                        </div>
                                    </div>
                                </Card>

                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div>
                                        Page {activitySubcoveragesPage} sur {activitySubcoveragesTotalPages}
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.First onClick={() => setActivitySubcoveragesPage(1)} disabled={activitySubcoveragesPage === 1} />
                                        <Pagination.Prev onClick={() => setActivitySubcoveragesPage((prev) => Math.max(prev - 1, 1))} disabled={activitySubcoveragesPage === 1} />
                                        {getActivitySubcoveragesPaginationRange().map((page) => (
                                            <Pagination.Item key={page} active={page === activitySubcoveragesPage} onClick={() => setActivitySubcoveragesPage(page)}>
                                                {page}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Next onClick={() => setActivitySubcoveragesPage((prev) => Math.min(prev + 1, activitySubcoveragesTotalPages))} disabled={activitySubcoveragesPage === activitySubcoveragesTotalPages} />
                                        <Pagination.Last onClick={() => setActivitySubcoveragesPage(activitySubcoveragesTotalPages)} disabled={activitySubcoveragesPage === activitySubcoveragesTotalPages} />
                                    </Pagination>
                                </div>

                                {/* Modal d'édition Activity Subcoverage */}
                                <Modal show={showActivityEditModal} onHide={() => setShowActivityEditModal(false)} size="lg">
                                    <Modal.Header closeButton>
                                        <Modal.Title>Modifier la sous-couverture</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Form id="activity-subcoverage-edit-form" onSubmit={handleActivityEditSubmit}>
                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Date de début</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            name="sub_coverage_start_date"
                                                            value={activitySubcoverageFormData.sub_coverage_start_date}
                                                            onChange={handleActivityEditChange}
                                                            required
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Date de fin</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            name="sub_coverage_end_date"
                                                            value={activitySubcoverageFormData.sub_coverage_end_date}
                                                            onChange={handleActivityEditChange}
                                                            required
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>Trigger low</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="trigger_low"
                                                            value={activitySubcoverageFormData.trigger_low}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>Trigger number hours</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            name="trigger_number_hours"
                                                            value={activitySubcoverageFormData.trigger_number_hours}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>Trigger hour inf</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="prcp_trigger_hour_inf"
                                                            value={activitySubcoverageFormData.prcp_trigger_hour_inf}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>Unité</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="unit"
                                                            value={activitySubcoverageFormData.unit}
                                                            onChange={handleActivityEditChange}
                                                            required
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Timing hour</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            name="timing_hour"
                                                            value={activitySubcoverageFormData.timing_hour}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Observation hour</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            name="observation_timing_hour"
                                                            value={activitySubcoverageFormData.observation_timing_hour}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Timing day</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            name="timing_day"
                                                            value={activitySubcoverageFormData.timing_day}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Observation day</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            name="observation_timing_day"
                                                            value={activitySubcoverageFormData.observation_timing_day}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>SIV Maintien</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="siv_ratio_maintained"
                                                            value={activitySubcoverageFormData.siv_ratio_maintained}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>SIV Annulation</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="siv_ratio_cancelled"
                                                            value={activitySubcoverageFormData.siv_ratio_cancelled}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>Type de déclencheur</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="trigger_type"
                                                            value={activitySubcoverageFormData.trigger_type}
                                                            onChange={handleActivityEditChange}
                                                            required
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Adresse activité</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="activity_address"
                                                            value={activitySubcoverageFormData.activity_address}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Ville</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="city"
                                                            value={activitySubcoverageFormData.city}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Code postal</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="zip_code"
                                                            value={activitySubcoverageFormData.zip_code}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Pays</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="country"
                                                            value={activitySubcoverageFormData.country}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Coverage code</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="coverage_code"
                                                            value={activitySubcoverageFormData.coverage_code}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Data source</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="data_source"
                                                            value={activitySubcoverageFormData.data_source}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Orakle ratio excl. TSCA</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="orakle_price_ratio_excl_tsca"
                                                            value={activitySubcoverageFormData.orakle_price_ratio_excl_tsca}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Longitude</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="geo_longitude"
                                                            value={activitySubcoverageFormData.geo_longitude}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Latitude</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="geo_latitude"
                                                            value={activitySubcoverageFormData.geo_latitude}
                                                            onChange={handleActivityEditChange}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Form>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => setShowActivityEditModal(false)}>
                                            Annuler
                                        </Button>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            form="activity-subcoverage-edit-form"
                                            disabled={updateActivitySubcoverage.loading}
                                        >
                                            {updateActivitySubcoverage.loading ? 'Enregistrement...' : 'Enregistrer'}
                                        </Button>
                                    </Modal.Footer>
                                </Modal>

                                {/* Modal de confirmation de suppression Activity Subcoverage */}
                                <Modal show={showActivityDeleteModal} onHide={handleCloseActivityDeleteModal}>
                                    <Modal.Header closeButton>
                                        <Modal.Title>Confirmer la suppression</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        {deleteActivitySubcoverage.error && (
                                            <Alert variant="danger" className="mb-3">
                                                {deleteActivitySubcoverage.error}
                                            </Alert>
                                        )}
                                        <p>
                                            Êtes-vous sûr de vouloir supprimer la sous-couverture{' '}
                                            <strong>
                                                {selectedActivitySubcoverageForDelete?.subcoverage?.coverage_code ||
                                                    selectedActivitySubcoverageForDelete?.subcoverage?.sub_coverage_id ||
                                                    'cette sous-couverture'}
                                            </strong> ?
                                        </p>
                                        <p className="text-muted mb-0">
                                            Cette action est irréversible.
                                        </p>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={handleCloseActivityDeleteModal} disabled={deleteActivitySubcoverage.loading}>
                                            Annuler
                                        </Button>
                                        <Button
                                            variant="danger"
                                            onClick={handleConfirmActivityDelete}
                                            disabled={deleteActivitySubcoverage.loading}
                                        >
                                            {deleteActivitySubcoverage.loading ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Suppression...
                                                </>
                                            ) : (
                                                <>
                                                    <FiTrash2 className="me-2" />
                                                    Supprimer
                                                </>
                                            )}
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                            </Tab.Pane>
                        </Tab.Content>

                        <ExportSubcoveragesTripModal
                            show={showTripSubcoveragesExportModal}
                            onHide={() => setShowTripSubcoveragesExportModal(false)}
                            onSuccess={(message) => setTripSubcoveragesExportSuccess(message)}
                        />
                    </Tab.Container>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminReference;
